//--------------------------------------------------------------------//
//******************** agentSchedulingTab *****************************//
//--------------------------------------------------------------------//
// Lists the agent's scheduled tasks in a table with an enable/disable
// switch (off by default) and a popup to create/edit a task's prompt
// and run schedule (days of week, start time, repeat frequency).
// The /schedule backend isn't ready yet, so the task list lives in
// local component state; AgentSchedulingService calls are best-effort and
// failures are swallowed so the UI stays usable.
import agentSchedulingTemplateUrl from './agent-scheduling.template.html';

angular.module('agentsTableModule')

.directive('agentSchedulingTab', ['GhDialog', 'AgentSchedulingService', function (GhDialog, AgentSchedulingService) {
    return {
        restrict: 'E',
        scope: {
            agent: '='
        },
        controller: ['$scope', 'localizationService', function ($scope, localizationService) {
            const dictionary = localizationService.getDictionary('account_settings');
            $scope.translate = (word) => localizationService.translate(word, dictionary);
            const translate = $scope.translate;

            $scope.weekDays = [
                { name: translate('Sun'), value: 0 },
                { name: translate('Mon'), value: 1 },
                { name: translate('Tue'), value: 2 },
                { name: translate('Wed'), value: 3 },
                { name: translate('Thu'), value: 4 },
                { name: translate('Fri'), value: 5 },
                { name: translate('Sat'), value: 6 }
            ];

            $scope.frequencyOptions = [
                { name: translate('A day'), value: 'daily' },
                { name: translate('An hour'), value: 'hourly' },
                { name: translate('30 minutes'), value: '30min' },
                { name: translate('10 minutes'), value: '10min' },
                { name: translate('1 minute'), value: '1min' }
            ];

            $scope.tasks = [];

            const agentId = $scope.agent._id;

            // Builds a cron expression from days of week, start time and repeat frequency.
            // Only called from saveTask() after validation, so startTime is always a
            // valid "HH:MM" string and daysOfWeek always has at least one entry.
            function buildCronExpression(daysOfWeek, startTime, frequency) {
                const [hourStr, minuteStr] = startTime.split(':');
                const hour = Number(hourStr);
                const minute = Number(minuteStr);
                const dow = daysOfWeek.slice().sort().join(',');

                switch (frequency) {
                    case '1min':
                        return `* * * * ${dow}`;
                    case '10min': {
                        const offset = minute % 10;
                        const slots = [0, 10, 20, 30, 40, 50].map(m => m + offset);
                        return `${slots.join(',')} * * * ${dow}`;
                    }
                    case '30min': {
                        const offset = minute % 30;
                        const slots = [0, 30].map(m => m + offset);
                        return `${slots.join(',')} * * * ${dow}`;
                    }
                    case 'hourly':
                        return `${minute} * * * ${dow}`;
                    case 'daily':
                    default:
                        return `${minute} ${hour} * * ${dow}`;
                }
            }

            // Builds a human-readable summary of a task's schedule for the table column.
            function describeSchedule(task) {
                const freqOption = $scope.frequencyOptions.find((f) => f.value === task.frequency);
                const freqLabel = freqOption ? freqOption.name : task.frequency;
                const days = task.daysOfWeek && task.daysOfWeek.length
                    ? task.daysOfWeek.slice().sort().map((v) => $scope.weekDays[v].name).join('/')
                    : translate('Every day');

                let timePart = '';
                if (task.frequency !== '1min' && task.startTime) {
                    timePart = `, ${translate('from')} ${task.startTime}`;
                }

                return `${translate('Every')} ${freqLabel}, ${days}${timePart}`;
            }
            $scope.describeSchedule = describeSchedule;

            // Loads scheduled tasks from the backend; falls back to the agent's
            // local schedules (or an empty list) since /schedule isn't live yet.
            async function loadSchedules() {
                try {
                    const result = await AgentSchedulingService.getSchedules(agentId);
                    $scope.tasks = (result && result.schedules) || $scope.agent.schedules || [];
                } catch (err) {
                    console.error('Failed to load schedules (backend not ready yet):', err);
                    $scope.tasks = $scope.agent.schedules || [];
                }
                $scope.$applyAsync();
            }
            loadSchedules();

            // gh-boolean's internal md-switch already flips task.enabled via ng-model
            // before this bubbled click handler runs, so we only stop the row click
            // from opening the dialog and best-effort sync the already-new value.
            $scope.toggleTask = function (e, task) {
                e.stopPropagation();

                AgentSchedulingService.toggleSchedule(agentId, task.id, task.enabled).catch((err) => {
                    console.error('Failed to toggle schedule (backend not ready yet):', err);
                });
            };

            // Removes a task locally and best-effort deletes it from the backend.
            function deleteTask(task) {
                const index = $scope.tasks.findIndex((t) => t.id === task.id);
                if (index !== -1) {
                    $scope.tasks.splice(index, 1);
                }

                AgentSchedulingService.deleteSchedule(agentId, task.id).catch((err) => {
                    console.error('Failed to delete schedule (backend not ready yet):', err);
                });
            }

            // Opens a confirmation dialog before deleting a scheduled task.
            $scope.removeTask = function (e, task) {
                e.stopPropagation();

                GhDialog.show({
                    position: 'center',
                    toolbar: false,
                    template: {
                        toolbar: '',
                        resizable: false,
                        content: `
                            <div class="delete_agent-popup">
                                <div class="dialog-share-group">
                                    <span gh-icon="rubbish FF0D0D 65px"></span>
                                    <p class="dialog-share-group__title">{{translate('Deleting Scheduled Task!')}}</p>
                                </div>

                                <div class="dialog-share-group__content">
                                    <p class="info">
                                        {{translate('Do you really want to delete this scheduled task? You will not be able to restore it.')}}
                                    </p>

                                    <div class="dialog-share-group__content__buttons">
                                        <button
                                            class="dialog-share-group__content__buttons__add btn btn-blue-reverse"
                                            ng-click="confirmDelete()"
                                        >
                                            {{translate('Delete')}}
                                        </button>

                                        <button
                                            class="dialog-share-group__content__buttons__cancel btn btn-blue"
                                            ng-click="cancel()"
                                        >
                                            {{translate('Cancel')}}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        `
                    },
                    locals: {
                        task
                    },
                    controller: ['$scope', 'task', 'localizationService', function ($dialogScope, task, localizationService) {
                        const dialogDictionary = localizationService.getDictionary('account_settings');
                        $dialogScope.translate = (word) => localizationService.translate(word, dialogDictionary);

                        $dialogScope.confirmDelete = function () {
                            deleteTask(task);
                            $dialogScope.cancel();
                        };
                    }]
                });
            };

            // Manually triggers a task immediately so it can be tested without waiting for its cron schedule.
            $scope.runTask = async function (e, task) {
                e.stopPropagation();
                if (task.isRunning) return;
                task.isRunning = true;
                $scope.$applyAsync();
                try {
                    await AgentSchedulingService.runSchedule(agentId, task.id);
                } catch (err) {
                    console.error('Failed to run task:', err);
                } finally {
                    task.isRunning = false;
                    $scope.$applyAsync();
                }
            };

            // Opens the add button.
            $scope.addTask = function () {
                $scope.openTaskDialog(null);
            };

            // Opens the create/edit popup for a scheduled task.
            $scope.openTaskDialog = function (task) {
                const isNew = !task;

                GhDialog.show({
                    position: 'center',
                    toolbar: false,
                    template: {
                        toolbar: '',
                        resizable: false,
                        content: `
                            <div class="agent-scheduling-dialog">
                                <p class="agent-scheduling-dialog__title">{{isNew ? translate('New Scheduled Task') : translate('Edit Scheduled Task')}}</p>

                                <div class="agent-scheduling-dialog__field" ng-class="{'agent-scheduling-dialog__field--error': submitted && errors.name}">
                                    <span>{{translate('Task Name')}}:</span>
                                    <gh-input class="agent-scheduling-dialog__name-input" ng-model="form.name" placeholder="{{translate('Task name')}}"></gh-input>
                                    <span class="agent-scheduling-dialog__error" ng-if="submitted && errors.name">{{translate('Required')}}</span>
                                </div>

                                <div class="agent-scheduling-dialog__field" ng-class="{'agent-scheduling-dialog__field--error': submitted && errors.prompt}">
                                    <span>{{translate('Prompt')}}:</span>
                                    <textarea class="agent-scheduling-dialog__prompt disable-gudhub-styles" ng-model="form.prompt" placeholder="{{translate('What should the agent do?')}}"></textarea>
                                    <span class="agent-scheduling-dialog__error" ng-if="submitted && errors.prompt">{{translate('Required')}}</span>
                                </div>

                                <div class="agent-scheduling-dialog__field" ng-class="{'agent-scheduling-dialog__field--error': submitted && errors.daysOfWeek}">
                                    <span>{{translate('Days of week')}}:</span>
                                    <div class="agent-scheduling-dialog__days">
                                        <div
                                            class="agent-scheduling-dialog__day"
                                            ng-repeat="day in weekDays"
                                            ng-class="{'agent-scheduling-dialog__day--active': form.daysOfWeek.indexOf(day.value) !== -1}"
                                            ng-click="toggleDay(day.value)"
                                        >{{day.name}}</div>
                                    </div>
                                    <span class="agent-scheduling-dialog__error" ng-if="submitted && errors.daysOfWeek">{{translate('Select at least one day')}}</span>
                                </div>

                                <div class="agent-scheduling-dialog__row">
                                    <div class="agent-scheduling-dialog__field" ng-class="{'agent-scheduling-dialog__field--error': submitted && errors.startTime}">
                                        <span>{{translate('Start time')}}:</span>
                                        <input class="agent-scheduling-dialog__time" type="time" ng-model="startTimeDate">
                                        <span class="agent-scheduling-dialog__error" ng-if="submitted && errors.startTime">{{translate('Required')}}</span>
                                    </div>

                                    <div class="agent-scheduling-dialog__field">
                                        <span>{{translate('Run once in')}}:</span>
                                        <select class="agent-scheduling-dialog__frequency" ng-model="form.frequency" ng-options="opt.value as opt.name for opt in frequencyOptions"></select>
                                    </div>
                                </div>

                                <div class="agent-scheduling-dialog__buttons">
                                    <button class="btn btn-blue" ng-disabled="!validate()" ng-click="saveTask()">{{translate('Save')}}</button>
                                    <button class="btn btn-blue-reverse" ng-click="cancel()">{{translate('Cancel')}}</button>
                                </div>
                            </div>
                        `
                    },
                    locals: {
                        task,
                        isNew,
                        weekDays: $scope.weekDays,
                        frequencyOptions: $scope.frequencyOptions
                    },
                    controller: ['$scope', 'task', 'isNew', 'weekDays', 'frequencyOptions', 'localizationService', function ($dialogScope, task, isNew, weekDays, frequencyOptions, localizationService) {
                        const dialogDictionary = localizationService.getDictionary('account_settings');
                        $dialogScope.translate = (word) => localizationService.translate(word, dialogDictionary);
                        $dialogScope.isNew = isNew;
                        $dialogScope.weekDays = weekDays;
                        $dialogScope.frequencyOptions = frequencyOptions;

                        $dialogScope.form = {
                            name: task ? task.name : '',
                            prompt: task ? task.prompt : '',
                            daysOfWeek: task ? task.daysOfWeek.slice() : [],
                            startTime: task ? task.startTime : '09:00',
                            frequency: task ? task.frequency : 'daily'
                        };

                        $dialogScope.toggleDay = function (value) {
                            const index = $dialogScope.form.daysOfWeek.indexOf(value);
                            if (index === -1) {
                                $dialogScope.form.daysOfWeek.push(value);
                            } else {
                                $dialogScope.form.daysOfWeek.splice(index, 1);
                            }
                        };

                        // The native <input type="time"> directive requires its ng-model to be
                        // a Date (or null), so this proxies it to/from form.startTime's "HH:MM" string.
                        // The Date instance is cached and only rebuilt when the string actually
                        // changes - ngModelWatch compares by reference, so returning a fresh Date
                        // on every digest call would never compare equal and loop forever.
                        let cachedStartTime = null;
                        let cachedStartTimeDate = null;

                        Object.defineProperty($dialogScope, 'startTimeDate', {
                            get: function () {
                                if ($dialogScope.form.startTime !== cachedStartTime) {
                                    cachedStartTime = $dialogScope.form.startTime;

                                    if (!cachedStartTime) {
                                        cachedStartTimeDate = null;
                                    } else {
                                        const [hourStr, minuteStr] = cachedStartTime.split(':');
                                        const date = new Date();
                                        date.setHours(Number(hourStr), Number(minuteStr), 0, 0);
                                        cachedStartTimeDate = date;
                                    }
                                }
                                return cachedStartTimeDate;
                            },
                            set: function (newDate) {
                                if (newDate instanceof Date && !isNaN(newDate)) {
                                    const hh = String(newDate.getHours()).padStart(2, '0');
                                    const mm = String(newDate.getMinutes()).padStart(2, '0');
                                    cachedStartTime = `${hh}:${mm}`;
                                    cachedStartTimeDate = newDate;
                                } else {
                                    cachedStartTime = '';
                                    cachedStartTimeDate = null;
                                }
                                $dialogScope.form.startTime = cachedStartTime;
                            }
                        });

                        $dialogScope.submitted = false;
                        $dialogScope.errors = {};

                        $dialogScope.validate = function () {
                            $dialogScope.errors = {
                                name: !$dialogScope.form.name || !$dialogScope.form.name.trim(),
                                prompt: !$dialogScope.form.prompt || !$dialogScope.form.prompt.trim(),
                                daysOfWeek: !$dialogScope.form.daysOfWeek.length,
                                startTime: !$dialogScope.form.startTime
                            };

                            return !Object.values($dialogScope.errors).some(Boolean);
                        };

                        $dialogScope.saveTask = async function () {
                            if (!$dialogScope.validate()) {
                                $dialogScope.submitted = true;
                                return;
                            }

                            const cron = buildCronExpression($dialogScope.form.daysOfWeek, $dialogScope.form.startTime, $dialogScope.form.frequency);

                            if (isNew) {
                                const newTask = {
                                    id: Date.now(),
                                    name: $dialogScope.form.name,
                                    prompt: $dialogScope.form.prompt,
                                    daysOfWeek: $dialogScope.form.daysOfWeek,
                                    startTime: $dialogScope.form.startTime,
                                    frequency: $dialogScope.form.frequency,
                                    cron,
                                    enabled: false
                                };

                                $scope.tasks.push(newTask);

                                try {
                                    await AgentSchedulingService.createSchedule(agentId, newTask);
                                } catch (err) {
                                    console.error('Failed to create schedule (backend not ready yet):', err);
                                }
                            } else {
                                Object.assign(task, {
                                    name: $dialogScope.form.name,
                                    prompt: $dialogScope.form.prompt,
                                    daysOfWeek: $dialogScope.form.daysOfWeek,
                                    startTime: $dialogScope.form.startTime,
                                    frequency: $dialogScope.form.frequency,
                                    cron
                                });

                                try {
                                    await AgentSchedulingService.updateSchedule(agentId, task.id, task);
                                } catch (err) {
                                    console.error('Failed to update schedule (backend not ready yet):', err);
                                }
                            }

                            $scope.$applyAsync();
                            $dialogScope.cancel();
                        };
                    }]
                });
            };
        }],
        templateUrl: agentSchedulingTemplateUrl
    };
}]);
